import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Image, Plus, Edit2, Trash2, Check, X, Eye, 
  ToggleLeft, ToggleRight, Palette, Link as LinkIcon
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';

const API = process.env.REACT_APP_BACKEND_URL;
const getAuthToken = () => localStorage.getItem('token');

const BannerManagement = () => {
  const [banners, setBanners] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBanner, setEditingBanner] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    subtitle: '',
    cta_text: 'Explore Jobs',
    cta_link: '',
    background_type: 'gradient',
    gradient_from: '#4C1D95',
    gradient_to: '#F97316',
    image_url: '',
    is_active: false
  });

  useEffect(() => {
    fetchBanners();
  }, []);

  const fetchBanners = async () => {
    try {
      const token = getAuthToken();
      const response = await axios.get(`${API}/job-banners`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBanners(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch banners:', error);
      toast.error('Failed to load banners');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!formData.title) {
      toast.error('Title is required');
      return;
    }

    try {
      const token = getAuthToken();
      const config = { headers: { Authorization: `Bearer ${token}` } };

      if (editingBanner) {
        await axios.patch(`${API}/admin/job-banners/${editingBanner.id}`, formData, config);
        toast.success('Banner updated');
      } else {
        await axios.post(`${API}/admin/job-banners`, formData, config);
        toast.success('Banner created');
      }

      setDialogOpen(false);
      setEditingBanner(null);
      resetForm();
      fetchBanners();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (bannerId) => {
    if (!window.confirm('Are you sure you want to delete this banner?')) return;

    try {
      const token = getAuthToken();
      await axios.delete(`${API}/admin/job-banners/${bannerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Banner deleted');
      fetchBanners();
    } catch (error) {
      toast.error('Failed to delete banner');
    }
  };

  const handleToggleActive = async (banner) => {
    try {
      const token = getAuthToken();
      await axios.post(`${API}/admin/job-banners/${banner.id}/toggle-active`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Banner ${!banner.is_active ? 'activated' : 'deactivated'}`);
      fetchBanners();
    } catch (error) {
      toast.error('Failed to toggle banner');
    }
  };

  const openEditDialog = (banner) => {
    setEditingBanner(banner);
    setFormData({
      title: banner.title || '',
      subtitle: banner.subtitle || '',
      cta_text: banner.cta_text || 'Explore Jobs',
      cta_link: banner.cta_link || '',
      background_type: banner.background_type || 'gradient',
      gradient_from: banner.gradient_from || '#4C1D95',
      gradient_to: banner.gradient_to || '#F97316',
      image_url: banner.image_url || '',
      is_active: banner.is_active || false
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      subtitle: '',
      cta_text: 'Explore Jobs',
      cta_link: '',
      background_type: 'gradient',
      gradient_from: '#4C1D95',
      gradient_to: '#F97316',
      image_url: '',
      is_active: false
    });
  };

  // Banner Preview
  const BannerPreview = ({ data }) => {
    const bgStyle = data.background_type === 'image' && data.image_url
      ? { backgroundImage: `url(${data.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' }
      : { background: `linear-gradient(135deg, ${data.gradient_from || '#4C1D95'} 0%, ${data.gradient_to || '#F97316'} 100%)` };

    return (
      <div 
        className="rounded-xl overflow-hidden h-32 relative"
        style={bgStyle}
      >
        <div className="absolute inset-0 bg-black/20 p-4 flex flex-col justify-center">
          <h3 className="text-white font-bold text-lg truncate">{data.title || 'Banner Title'}</h3>
          {data.subtitle && (
            <p className="text-white/80 text-sm truncate">{data.subtitle}</p>
          )}
          {data.cta_text && (
            <span className="mt-2 inline-block bg-white text-sm px-3 py-1 rounded-lg w-fit">
              {data.cta_text}
            </span>
          )}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-48">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-bold text-slate-900">Job Hero Banners</h2>
          <p className="text-sm text-slate-500">Manage banners displayed on the Lead Specialist job dashboard</p>
        </div>
        <Button 
          onClick={() => { resetForm(); setEditingBanner(null); setDialogOpen(true); }}
          className="bg-gradient-to-r from-violet-500 to-purple-600 hover:from-violet-600 hover:to-purple-700 rounded-xl"
          data-testid="create-banner-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Banner
        </Button>
      </div>

      {/* Banners Grid */}
      {banners.length === 0 ? (
        <Card className="border-dashed border-2 rounded-2xl">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <div className="h-16 w-16 rounded-2xl bg-violet-100 flex items-center justify-center mb-4">
              <Image className="h-8 w-8 text-violet-500" />
            </div>
            <h3 className="font-semibold text-lg text-slate-900 mb-2">No banners yet</h3>
            <p className="text-slate-500 text-sm text-center max-w-sm mb-4">
              Create your first hero banner to display on the Lead Specialist job dashboard
            </p>
            <Button 
              variant="outline"
              onClick={() => { resetForm(); setEditingBanner(null); setDialogOpen(true); }}
              className="rounded-xl"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create First Banner
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {(banners || []).map((banner) => (
            <Card key={banner.id} className="rounded-2xl overflow-hidden border-slate-200">
              <CardContent className="p-0">
                {/* Preview */}
                <BannerPreview data={banner} />
                
                {/* Info & Actions */}
                <div className="p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      {banner.is_active ? (
                        <Badge className="bg-emerald-100 text-emerald-700">Active</Badge>
                      ) : (
                        <Badge className="bg-slate-100 text-slate-600">Inactive</Badge>
                      )}
                      <Badge variant="outline" className="capitalize">
                        {banner.background_type}
                      </Badge>
                    </div>
                    <button
                      onClick={() => handleToggleActive(banner)}
                      className="text-slate-400 hover:text-slate-600 transition-colors"
                      title={banner.is_active ? 'Deactivate' : 'Activate'}
                    >
                      {banner.is_active ? (
                        <ToggleRight className="h-6 w-6 text-emerald-500" />
                      ) : (
                        <ToggleLeft className="h-6 w-6" />
                      )}
                    </button>
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => openEditDialog(banner)}
                      className="flex-1 rounded-xl"
                    >
                      <Edit2 className="h-4 w-4 mr-1" />
                      Edit
                    </Button>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => handleDelete(banner.id)}
                      className="rounded-xl text-red-600 hover:bg-red-50 hover:text-red-700"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl rounded-2xl">
          <DialogHeader>
            <DialogTitle>
              {editingBanner ? 'Edit Banner' : 'Create New Banner'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Preview */}
            <div>
              <Label className="text-sm font-medium text-slate-700 mb-2 block">Preview</Label>
              <BannerPreview data={formData} />
            </div>

            {/* Title */}
            <div>
              <Label htmlFor="title">Title *</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Find Your Next Opportunity"
                className="rounded-xl mt-1"
              />
            </div>

            {/* Subtitle */}
            <div>
              <Label htmlFor="subtitle">Subtitle</Label>
              <Textarea
                id="subtitle"
                value={formData.subtitle}
                onChange={(e) => setFormData({ ...formData, subtitle: e.target.value })}
                placeholder="Refer top candidates and earn rewards..."
                className="rounded-xl mt-1"
                rows={2}
              />
            </div>

            {/* CTA */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="cta_text">CTA Button Text</Label>
                <Input
                  id="cta_text"
                  value={formData.cta_text}
                  onChange={(e) => setFormData({ ...formData, cta_text: e.target.value })}
                  placeholder="Explore Jobs"
                  className="rounded-xl mt-1"
                />
              </div>
              <div>
                <Label htmlFor="cta_link">CTA Link (optional)</Label>
                <Input
                  id="cta_link"
                  value={formData.cta_link}
                  onChange={(e) => setFormData({ ...formData, cta_link: e.target.value })}
                  placeholder="https://..."
                  className="rounded-xl mt-1"
                />
              </div>
            </div>

            {/* Background Type */}
            <div>
              <Label>Background Type</Label>
              <Select 
                value={formData.background_type} 
                onValueChange={(value) => setFormData({ ...formData, background_type: value })}
              >
                <SelectTrigger className="rounded-xl mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="gradient">Gradient</SelectItem>
                  <SelectItem value="image">Image URL</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Gradient Colors or Image URL */}
            {formData.background_type === 'gradient' ? (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="gradient_from">Gradient Start Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={formData.gradient_from}
                      onChange={(e) => setFormData({ ...formData, gradient_from: e.target.value })}
                      className="h-10 w-16 rounded cursor-pointer"
                    />
                    <Input
                      value={formData.gradient_from}
                      onChange={(e) => setFormData({ ...formData, gradient_from: e.target.value })}
                      className="rounded-xl flex-1"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="gradient_to">Gradient End Color</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="color"
                      value={formData.gradient_to}
                      onChange={(e) => setFormData({ ...formData, gradient_to: e.target.value })}
                      className="h-10 w-16 rounded cursor-pointer"
                    />
                    <Input
                      value={formData.gradient_to}
                      onChange={(e) => setFormData({ ...formData, gradient_to: e.target.value })}
                      className="rounded-xl flex-1"
                    />
                  </div>
                </div>
              </div>
            ) : (
              <div>
                <Label htmlFor="image_url">Background Image URL</Label>
                <Input
                  id="image_url"
                  value={formData.image_url}
                  onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
                  placeholder="https://example.com/image.jpg"
                  className="rounded-xl mt-1"
                />
              </div>
            )}

            {/* Active Toggle */}
            <div className="flex items-center justify-between p-4 bg-slate-50 rounded-xl">
              <div>
                <p className="font-medium text-slate-900">Set as Active Banner</p>
                <p className="text-sm text-slate-500">Only one banner can be active at a time</p>
              </div>
              <button
                type="button"
                onClick={() => setFormData({ ...formData, is_active: !formData.is_active })}
                className="focus:outline-none"
              >
                {formData.is_active ? (
                  <ToggleRight className="h-8 w-8 text-emerald-500" />
                ) : (
                  <ToggleLeft className="h-8 w-8 text-slate-400" />
                )}
              </button>
            </div>
          </div>

          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setDialogOpen(false)}
              className="rounded-xl"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              className="bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl"
            >
              {editingBanner ? 'Save Changes' : 'Create Banner'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BannerManagement;
